import { SUBMISSION_TYPE_EVEN_FINANCE_PREQUAL } from "@shared/constants";

import getProviderOffers from "@client/functions/getProviderOffers";
import getSortedSecondaryLoanOffers from "@client/functions/getSortedSecondaryLoanOffers";
import mapConsolidationLoanOffers from "@client/routes/journey/thank-you/functions/mapConsolidationLoanOffers";
import mapDebtSettlementOffers from "@client/routes/journey/thank-you/functions/mapDebtSettlementOffers";

import type PrimaryOffer from "@packages/types/front-end-api/primary-offer";
import type { LeadSubmissionResponse } from "@packages/types/front-end-api/lead-submission";

export const OFFER_TYPES = {
  BEYOND_DOWN: "beyond_down",
  BEYOND_STATE_ONLY: "beyond_state_only",
  CAMBRIDGE_STANDARD: "cambridge_standard",
  EVEN_STANDARD: "even_standard",
  MONEYLION_DEBT_STANDARD: "moneylion_debt_standard",
} as const;

export type NoPartnersVariant =
  | "adr-debt-settlement"
  | "cambridge"
  | "moneylion-consolidation";

const NO_PARTNERS_DEBT_THRESHOLD = 20_000;
const NO_PARTNERS_INCOME_THRESHOLD = 50_000;
export const MAX_CONSOLIDATION_LOAN_OFFERS = 5;

const DEBT_BUCKET_AMOUNTS: Record<string, number> = {
  "$10k_to_$20k": 15_000,
  "$20k_to_$40k": 30_000,
  "$40k_to_$100k": 70_000,
  over_$100k: 150_000,
};

function getPrimaryOffers(
  leadSubmission: LeadSubmissionResponse,
): PrimaryOffer[] {
  return leadSubmission?.lead_submission?.data?.offers ?? [];
}

function getPrimaryOfferType(leadSubmission: LeadSubmissionResponse) {
  return getPrimaryOffers(leadSubmission)[0]?.offer_type;
}

export function getMoneylionDebtSettlementOffers(
  leadSubmission: LeadSubmissionResponse,
) {
  return mapDebtSettlementOffers(getPrimaryOffers(leadSubmission));
}

export function getMoneylionConsolidationLoanOffers(
  leadSubmission: LeadSubmissionResponse,
) {
  const secondaryOffers = getProviderOffers(
    leadSubmission?.lead_submission,
    SUBMISSION_TYPE_EVEN_FINANCE_PREQUAL,
  );

  return mapConsolidationLoanOffers(
    getSortedSecondaryLoanOffers(secondaryOffers).slice(
      0,
      MAX_CONSOLIDATION_LOAN_OFFERS,
    ),
  );
}

export function shouldShowMoneylionDebtSettlement(
  leadSubmission: LeadSubmissionResponse,
) {
  return getMoneylionDebtSettlementOffers(leadSubmission).length > 0;
}

export function shouldShowMoneylionConsolidationOffers(
  leadSubmission: LeadSubmissionResponse,
) {
  return (
    getPrimaryOfferType(leadSubmission) === OFFER_TYPES.EVEN_STANDARD &&
    getMoneylionConsolidationLoanOffers(leadSubmission).length > 0
  );
}

export function shouldShowAdrRedirect(leadSubmission: LeadSubmissionResponse) {
  return (
    getPrimaryOfferType(leadSubmission) === OFFER_TYPES.BEYOND_STATE_ONLY
  );
}

export function shouldShowCambridgeStandard(
  leadSubmission: LeadSubmissionResponse,
) {
  return getPrimaryOfferType(leadSubmission) === OFFER_TYPES.CAMBRIDGE_STANDARD;
}

export function getCambridgeCtaHref(leadSubmission: LeadSubmissionResponse) {
  return getPrimaryOffers(leadSubmission)[0]?.link;
}

export function getAdrRedirectUrl(leadSubmission: LeadSubmissionResponse) {
  return getPrimaryOffers(leadSubmission)[0]?.link;
}

export function shouldShowNoPartnersFlow(
  leadSubmission: LeadSubmissionResponse,
) {
  const primaryOfferType = getPrimaryOfferType(leadSubmission);

  if (primaryOfferType !== OFFER_TYPES.EVEN_STANDARD) {
    return false;
  }

  return !shouldShowMoneylionConsolidationOffers(leadSubmission);
}

function parseNumericValue(value: unknown) {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.replace(/[^0-9.-]/g, ""));

    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return undefined;
}

function parseDebtBucket(value: unknown) {
  if (typeof value === "string" && value in DEBT_BUCKET_AMOUNTS) {
    return DEBT_BUCKET_AMOUNTS[value];
  }

  return undefined;
}

function getDebtAmount(leadSubmission: LeadSubmissionResponse) {
  return (
    parseNumericValue(
      leadSubmission?.lead_submission?.data?.soft_pull_data?.debt_amount,
    ) ??
    parseNumericValue(
      leadSubmission?.lead_submission?.data?.financial?.debt_amount,
    ) ??
    parseDebtBucket(leadSubmission?.lead_submission_draft?.data?.debtAmount)
  );
}

function getIncomeAmount(leadSubmission: LeadSubmissionResponse) {
  return parseNumericValue(
    leadSubmission?.lead_submission_draft?.data?.incomeAmount,
  );
}

export function resolveNoPartnersVariant(
  leadSubmission: LeadSubmissionResponse,
): NoPartnersVariant {
  const debtAmount = getDebtAmount(leadSubmission);
  const incomeAmount = getIncomeAmount(leadSubmission);

  if (
    typeof debtAmount === "number" &&
    debtAmount > NO_PARTNERS_DEBT_THRESHOLD
  ) {
    return "adr-debt-settlement";
  }

  if (
    typeof incomeAmount === "number" &&
    incomeAmount > NO_PARTNERS_INCOME_THRESHOLD
  ) {
    return "moneylion-consolidation";
  }

  return "cambridge";
}

